import { useTransactionCalculations } from '../hooks/useTransactionCalculations';
import { ProductPayment } from '../types/transactionTypes';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcEqual from '@/assets/icons/ic-equal.svg';
import IcForwardArrow from '@/assets/icons/ic-arrow-right.svg';
import IcMinus from '@/assets/icons/ic-minus.svg';
import IcHelp from '@/assets/icons/ic-help-circle.svg';
import { StanTooltip } from '@/components';
import { calculatePaymentAmounts, isStatusPaid } from '../income.logic';
interface TransactionPaymentDetailsProps {
    paymentData: ProductPayment;
}

export function TransactionPaymentDetails({ paymentData }: TransactionPaymentDetailsProps) {
    const {
        paymentMethodIcon: PaymentMethodIcon,
        paymentGatewayDisplayName,
        posTaxRevenue,
        posPreConversionAmountDisplay,
        showTaxAmount,
        showGrossAmount,
        tooltipContent,
    } = useTransactionCalculations(paymentData);

    const isPaidStatus = isStatusPaid(paymentData.status);
    const { posRevenue, posGrossAmount, posFeeAmount, posNetRevenue } = calculatePaymentAmounts(paymentData);

    return (
        <div className='flex w-full flex-col flex-wrap items-center gap-4 md:flex-row md:items-start md:gap-7'>
            <div className='flex w-full flex-col flex-wrap gap-4 md:flex-grow md:flex-row md:gap-7'>
                {/* PRICE + TAX */}
                {showTaxAmount && (
                    <>
                        {/* Price */}
                        <div className='flex w-full flex-grow-0 flex-row items-start gap-2 pl-5 md:w-auto md:flex-col md:pl-0'>
                            <div className='inline-flex h-full w-3 items-center pr-1 md:hidden' />
                            <div className='text-gray'>Price</div>
                            <div className='text-dark font-bold'>{posRevenue}</div>
                        </div>

                        {/* "+" icon (desktop only) */}
                        <div className='mt-auto hidden flex-grow-0 pl-0 md:flex md:pb-2'>
                            <IcPlus />
                        </div>

                        {/* Tax Collected */}
                        <div className='flex w-full flex-grow-0 flex-row items-center gap-2 pl-5 md:w-auto md:flex-col md:pl-0'>
                            <div className='inline-flex h-full items-center pr-1 md:hidden'>
                                <IcPlus />
                            </div>
                            <div className='text-gray'>Tax Collected</div>
                            <div className='text-dark font-bold'>{posTaxRevenue}</div>
                        </div>

                        {/* "=" icon (desktop only) */}
                        <div className='mt-auto hidden flex-grow-0 pl-0 md:flex md:pb-2'>
                            <IcEqual />
                        </div>
                    </>
                )}

                {/* AMOUNT COLLECTED */}
                <div className='flex w-full flex-grow-0 flex-row items-center gap-2 md:w-auto md:flex-col md:items-start'>
                    <div className='inline-flex h-full items-center pr-1 md:hidden'>
                        {showTaxAmount ? <IcEqual /> : <div className='w-3' />}
                    </div>
                    <div className='text-gray text-sm font-semibold'>Amount Collected</div>
                    <div className='text-dark ml-auto inline-flex flex-wrap items-center font-bold md:ml-0'>
                        <PaymentMethodIcon className='mr-2 rounded-sm' width={21} height={21} />
                        {posPreConversionAmountDisplay}
                    </div>
                </div>

                {/* CONVERSION */}
                {showGrossAmount && (
                    <>
                        <div className='mt-auto hidden h-6 items-center pl-0 md:flex'>
                            <IcForwardArrow />
                        </div>
                        <div className='flex w-full flex-row items-center gap-2 md:w-auto md:flex-col md:items-start'>
                            <div className='inline-flex h-full items-center pr-1 md:hidden'>
                                <IcForwardArrow />
                            </div>
                            <div className='text-gray text-sm font-semibold'>Conversion</div>
                            <div className='text-dark ml-auto font-medium md:ml-0'>{posGrossAmount}</div>
                        </div>
                    </>
                )}

                {/* FEES & NET (if paid) */}
                {isPaidStatus && (
                    <>
                        {posFeeAmount && (
                            <>
                                <div className='mt-auto hidden h-6 items-center pl-0 md:flex'>
                                    <IcMinus />
                                </div>
                                <div className='flex w-full flex-row items-center gap-2 md:w-auto md:flex-col md:items-start'>
                                    <div className='h-full items-center pr-1 md:hidden'>
                                        <IcMinus />
                                    </div>
                                    <div className='text-gray inline-flex items-center text-sm font-semibold'>
                                        {`${paymentGatewayDisplayName}'s Processing Fee`}
                                        <StanTooltip
                                            className='mx-2 inline-flex items-center'
                                            content={tooltipContent}
                                            trigger='click'
                                        >
                                            <IcHelp className='cursor-pointer' width={14} height={14} />
                                        </StanTooltip>
                                    </div>
                                    <div className='text-dark ml-auto font-medium md:ml-0'>{posFeeAmount}</div>
                                </div>
                            </>
                        )}

                        {/* Net Revenue */}
                        {posNetRevenue && (
                            <>
                                <div className='mt-auto hidden h-6 items-center pl-0 md:flex'>
                                    <IcEqual />
                                </div>
                                <div className='border-neutral-soft flex w-full flex-row items-center gap-2 border-t pt-2 md:w-fit md:flex-col md:items-start md:border-0 md:pt-0 md:pl-0'>
                                    <div className='inline-flex h-full items-center pr-1 md:hidden'>
                                        <IcEqual />
                                    </div>
                                    <div className='text-gray text-sm font-semibold'>Net Revenue</div>
                                    <div className='text-dark ml-auto font-bold md:ml-0'>{posNetRevenue}</div>
                                </div>
                            </>
                        )}
                    </>
                )}
                <div className='border-neutral-soft flex-grow-0 md:border-l'>
                    <div className='bg-neutral-subtle mx-2 rounded-md p-4 md:mx-0 md:w-64 md:rounded-none md:bg-transparent md:p-0 md:pl-4'>
                        <div className='text-gray text-xs'>
                            <span className='font-bold'>Stan takes 0% of your transactions.</span>&nbsp; However, the
                            payment processors each have their own fees.&nbsp;
                            <a
                                href='https://stripe.com/en-ca/pricing'
                                target='_blank'
                                rel='noreferrer'
                                className='text-gray underline'
                            >
                                Learn&nbsp;More
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
